import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, ArrowUpRight, Car, ArrowDown } from "lucide-react";
import { BRAND, FOOD, SCENE } from "@/data/media";
import { SITE, CULINARY_SIGNATURE, MANIFESTO } from "@/data/site";
import { Reveal, LineReveal, FadeIn } from "@/components/motion";
import Particles from "@/components/Particles";

const EASE = [0.22, 1, 0.36, 1];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const textY = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section ref={ref} className="relative min-h-[100svh] bg-ink overflow-hidden flex flex-col justify-between" data-testid="hero">
      <Particles count={16} />

      {/* main */}
      <div className="relative z-10 u-container flex-1 flex items-center pt-28 md:pt-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 w-full items-center">
          <motion.div style={{ y: textY }} className="lg:col-span-7 flex flex-col items-start">
            <div className="flex flex-col items-start w-fit">
              {/* Centered Logo in Hero */}
              <FadeIn delay={0.2} className="w-full flex justify-center mb-6 md:mb-8">
                <Link
                  to="/"
                  className="flex items-center justify-center border border-gold/80 rounded-full p-1.5 h-24 w-24 md:h-36 md:w-36 shrink-0 aspect-square hover:border-cream hover:scale-105 transition-all duration-300 overflow-hidden bg-ink/10"
                >
                  <img
                    src="/images/logo_new_white_clean.png"
                    alt="Chef Jomon's #VEEDU"
                    className="h-full w-full object-contain"
                    style={{ position: "relative", left: "12%", top: "8%" }}
                  />
                </Link>
              </FadeIn>

              <FadeIn delay={0.3}>
                <div className="flex items-center gap-3 mb-6">
                  <span className="w-12 h-px bg-gold" />
                  <span className="u-eyebrow text-gold/80">Hornchurch · Essex · est. Kerala</span>
                </div>
              </FadeIn>
              <motion.span
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 1 }}
                className="font-script text-gold text-3xl md:text-5xl block mb-1"
              >
                Come and listen
              </motion.span>
              <LineReveal
                trigger="load"
                delay={0.6}
                lines={["TO THE FOOD TALES OF KERALA"]}
                lineClassName="font-body uppercase tracking-[0.4em] text-cream/45 text-[10px] md:text-sm mb-3 md:mb-4"
              />
              <LineReveal
                trigger="load"
                delay={0.75}
                lines={["#VEEDU"]}
                lineClassName="font-display text-cream text-[15vw] lg:text-[7.5vw] leading-[0.82] tracking-tight"
              />
              <FadeIn delay={1.2}>
                <span className="mt-3 block w-24 h-px bg-gradient-to-r from-gold to-transparent" />
              </FadeIn>
            </div>
          </motion.div>

          <div className="lg:col-span-5 relative">
            <motion.div
              style={{ y: imgY, height: "min(55vh, 550px)" }}
              initial={{ opacity: 0, scale: 1.04 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.5, delay: 0.7, ease: EASE }}
              className="relative w-full overflow-hidden border border-gold/25 bg-charcoal"
            >
              <video
                src="/Videos/hospitality.mp4"
                autoPlay
                muted
                loop
                playsInline
                className="w-full h-full object-cover kenburns"
              />
              <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-ink/90 to-transparent flex items-end justify-between">
                <span className="font-display text-xl text-cream">#VEEDU</span>
                <span className="u-eyebrow text-gold">hospitality</span>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.5, duration: 1 }}
              className="hidden md:block absolute -left-12 -bottom-12 w-36 lg:w-44 aspect-[4/5] overflow-hidden border border-gold/40 shadow-2xl"
            >
              <video
                src="/Videos/hospitality2.mp4"
                autoPlay
                muted
                loop
                playsInline
                className="w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </div>

      {/* bottom strip */}
      <FadeIn delay={1.6} className="relative z-10 border-t border-cream/10 bg-ink/40">
        <div className="u-container py-5 md:py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <p className="font-body font-light text-cream/70 text-sm md:text-base leading-relaxed max-w-md md:pl-28">
            {SITE.heroSupport}
          </p>

          <div className="text-left md:text-right space-y-1.5 shrink-0">
            <div className="flex md:justify-end items-center gap-2 font-body text-xs md:text-sm text-cream/80 font-medium">
              <MapPin size={14} className="text-terracotta shrink-0" />
              <span>{SITE.address.line1}, {SITE.address.postcode}</span>
            </div>
            <div className="flex md:justify-end items-center gap-2 font-body text-xs text-cream/50">
              <Car size={14} className="text-gold shrink-0" />
              <span>{SITE.parking}</span>
            </div>
          </div>
        </div>
      </FadeIn>

      <div className="pointer-events-none absolute left-6 md:left-12 bottom-28 md:bottom-32 z-20 hidden sm:block">
        <div className="relative w-24 h-24">
          <svg viewBox="0 0 100 100" className="spin-slow w-full h-full">
            <defs>
              <path id="circlePath" d="M50,50 m-34,0 a34,34 0 1,1 68,0 a34,34 0 1,1 -68,0" />
            </defs>
            <text className="fill-gold/60" style={{ fontSize: "9px", letterSpacing: "3px" }}>
              <textPath href="#circlePath">SCROLL DOWN · SCROLL DOWN · </textPath>
            </text>
          </svg>
          <ArrowDown size={16} className="text-gold absolute inset-0 m-auto" />
        </div>
      </div>
    </section>
  );
}



function FullBleed() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  return (
    <section ref={ref} className="relative h-[80vh] md:h-screen overflow-hidden bg-ink" data-testid="home-fullbleed">
      <motion.img
        src="/images/indoor.png"
        alt="Intimate, dark #VEEDU dining room"
        style={{ y, scale: 1.2 }}
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/50 to-ink/10" />
      <div className="relative z-10 h-full u-container flex items-center">
        <div className="w-full max-w-4xl">
          <Reveal>
            <span className="w-12 h-px bg-gold block mb-6" />
            <h2 className="font-display text-4xl md:text-6xl leading-[1.05] text-cream mb-6">
              Two cultures,<br />one roof.
            </h2>
            <p className="font-body font-light text-base md:text-lg leading-relaxed text-cream/75 max-w-2xl">
              Standing amid the ancient beams of an Essex landmark, the mind drifts back to the timeless Kerala
              <span className="font-display italic text-gold"> nalukettu</span> — a place where stories, traditions, and generations gather beneath one roof. Here, two cultures meet naturally, bringing together the warmth of Kerala and the character of Essex in a space shaped by heritage, hospitality, and the shared joy of gathering around a table.
            </p>
            <p className="font-script text-gold text-3xl mt-6">two cultures, one roof</p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}


function FoodSection() {
  return (
    <section className="bg-ink text-goldlight py-12 md:py-16 relative overflow-hidden" data-testid="home-food">
      <div className="u-container">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
          <div>
            <Reveal>
              <p className="u-eyebrow text-terracotta mb-4">From Kerala, with stories</p>
            </Reveal>
            <LineReveal lines={["Food with", "a memory."]} lineClassName="font-display text-5xl md:text-7xl leading-[0.95] text-cream" />
          </div>
          <Reveal delay={0.2}>
            <p className="font-body font-light text-goldlight/50 max-w-sm text-base leading-relaxed">
              Coastal fire, roasted spice, coconut and slow cooking — plated with the quiet confidence of a home
              kitchen and the precision of a chef's table.
            </p>
          </Reveal>
        </div>

        {/* asymmetric editorial grid */}
        <div className="grid grid-cols-12 gap-4 md:gap-6">
          <Reveal className="col-span-12 md:col-span-7">
            <figure className="group relative overflow-hidden aspect-[16/11]">
              <img src={FOOD.fishCurry} alt="Kottayam fish curry" loading="lazy" className="w-full h-full object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.06]" />
              <figcaption className="absolute bottom-0 inset-x-0 p-6 bg-gradient-to-t from-ink/90 to-transparent">
                <span className="u-eyebrow text-gold">Coastal</span>
                <p className="font-display text-2xl md:text-3xl text-cream">Kottayam Fish Curry</p>
              </figcaption>
            </figure>
          </Reveal>
          <Reveal delay={0.1} className="col-span-6 md:col-span-5">
            <figure className="group relative overflow-hidden aspect-[16/11] md:mt-16">
              <img src={SCENE.seafoodPlatter} alt="Kerala seafood platter" loading="lazy" className="w-full h-full object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.06]" />
              <figcaption className="absolute bottom-0 inset-x-0 p-6 bg-gradient-to-t from-ink/90 to-transparent">
                <span className="u-eyebrow text-gold">From the coast</span>
                <p className="font-display text-2xl md:text-3xl text-cream">Malabar Seafood</p>
              </figcaption>
            </figure>
          </Reveal>
          <Reveal delay={0.05} className="col-span-6 md:col-span-4">
            <figure className="group relative overflow-hidden aspect-[3/4]">
              <img src={FOOD.tandoorLamb} alt="Himalayan spiced lamb cutlets" loading="lazy" className="w-full h-full object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.06]" />
              <figcaption className="absolute bottom-0 inset-x-0 p-5 bg-gradient-to-t from-ink/90 to-transparent">
                <p className="font-display text-xl text-cream">From the Fire</p>
              </figcaption>
            </figure>
          </Reveal>
          <Reveal delay={0.1} className="col-span-12 md:col-span-4">
            <figure className="group relative overflow-hidden aspect-[3/4] md:-mt-10">
              <img src={FOOD.biriyani} alt="Malabar biriyani" loading="lazy" className="w-full h-full object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.06]" />
              <figcaption className="absolute bottom-0 inset-x-0 p-5 bg-gradient-to-t from-ink/90 to-transparent">
                <p className="font-display text-xl text-cream">Malabar Biriyani</p>
              </figcaption>
            </figure>
          </Reveal>
          <Reveal delay={0.15} className="col-span-6 md:col-span-4">
            <figure className="group relative overflow-hidden aspect-[3/4]">
              <img src={FOOD.scallops} alt="Malabar coast scallops" loading="lazy" className="w-full h-full object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.06]" />
              <figcaption className="absolute bottom-0 inset-x-0 p-5 bg-gradient-to-t from-ink/90 to-transparent">
                <p className="font-display text-xl text-cream">Coast Scallops</p>
              </figcaption>
            </figure>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function SignatureMarquee() {
  const words = ["From the Coast", "From the Fire", "From the Land", "From the Tandoor", "From the Kitchen"];
  const loop = [...words, ...words];
  return (
    <section className="bg-brown text-cream py-10 md:py-14 overflow-hidden" data-testid="home-signature">
      <div className="u-container mb-6">
        <Reveal>
          <p className="u-eyebrow text-gold">Culinary signature</p>
        </Reveal>
      </div>
      <div className="relative flex whitespace-nowrap animate-marquee">
        {loop.map((w, i) => (
          <span key={i} className="inline-flex items-center">
            <span className="font-display italic text-5xl md:text-8xl px-8 text-cream/90">{w}</span>
            <span className="w-2 h-2 rounded-full bg-terracotta mx-2" />
          </span>
        ))}
      </div>
      <div className="u-container grid grid-cols-2 md:grid-cols-5 gap-8 mt-8">
        {CULINARY_SIGNATURE.map((c, i) => (
          <Reveal key={c.n} delay={i * 0.08}>
            <div className="border-t border-gold/30 pt-4">
              <span className="font-body text-xs text-gold tracking-widest">{c.n}</span>
              <h4 className="font-display text-2xl mt-1">{c.title}</h4>
              <p className="font-body font-light text-cream/50 text-sm mt-2 leading-relaxed">{c.note}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function BrandWorld() {
  const cards = [
    { src: "/images/1.png", label: "The Fire" },
    { src: "/images/2.png", label: "The Craft" },
    { src: "/images/3.jpg", label: "The Feast" },
    { src: SCENE.coconutCurryleaf, label: "The Ingredients" },
  ];
  return (
    <section className="bg-charcoal text-cream py-12 md:py-16" data-testid="home-brandworld">
      <div className="u-container">
        <div className="max-w-2xl mb-8">
          <Reveal>
            <p className="u-eyebrow text-gold mb-4">The world of #VEEDU</p>
            <h2 className="font-display text-4xl md:text-6xl leading-[1.02]">
              A house, a kitchen, a table — and the objects that carry the story.
            </h2>
          </Reveal>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {cards.map((c, i) => (
            <Reveal key={c.label} delay={i * 0.1}>
              <figure className="group relative overflow-hidden aspect-[4/5] bg-ink">
                <img
                  src={c.src}
                  alt={c.label}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-[1300ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.05]"
                />
                <figcaption className="absolute bottom-0 inset-x-0 p-5 flex items-center justify-between bg-gradient-to-t from-ink/90 to-transparent">
                  <span className="font-display text-xl text-cream drop-shadow">{c.label}</span>
                  <span className="w-8 h-px bg-gold group-hover:w-14 transition-all duration-500" />
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Closing() {
  return (
    <section className="bg-ink text-cream py-8 md:py-10 relative overflow-hidden" data-testid="home-closing">
      <div className="u-container text-center relative">
        <div className="max-w-4xl mx-auto space-y-4">
          <p className="u-eyebrow text-gold tracking-[0.3em]">Come and listen to the food tales of Kerala</p>
          <h3 className="font-display text-2xl md:text-4xl text-cream leading-tight">
            A house in Hornchurch. A heart from Kerala. A table for everyone.
          </h3>
          <div className="pt-1">
            <Link to="/story" data-testid="closing-cta" className="btn-veedu border-cream/40 text-cream hover:bg-terracotta hover:border-terracotta py-2.5 px-6 text-xs">
              Explore #VEEDU <ArrowUpRight size={14} />
            </Link>
          </div>

          <div className="pt-6 grid grid-cols-2 md:grid-cols-5 gap-4 text-left border-t border-cream/10 mt-4">
            {MANIFESTO.map((m, i) => (
              <Reveal key={m.n} delay={i * 0.05}>
                <div>
                  <span className="font-display italic text-gold text-sm">{m.n}</span>
                  <p className="font-body font-light text-cream/70 text-xs mt-1 leading-snug">{m.line}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="page-home">
      <Hero />
      <FullBleed />
      <FoodSection />
      <SignatureMarquee />
      <BrandWorld />
      <Closing />
    </div>
  );
}
